// @flow
import type { IConstraintModel } from "../../types";

const INVALID_LENGTH_ID = "Constraint.InvalidLengthExact";
const INVALID_LENGTH_BETWEEN_ID = "Constraint.InvalidLengthBetween";
const INVALID_LENGTH_TOO_SHORT_ID = "Constraint.InvalidLengthTooShort";
const INVALID_LENGTH_TOO_LONG_ID = "Constraint.InvalidLengthTooLong";

/**
 */
class StringLengthConstraint implements IConstraintModel {
  _minLength: number | null;
  _maxLength: number | null;

  /**
   */
  constructor(minLength: number | null, maxLength: number | null) {
    this._minLength = minLength;
    this._maxLength = maxLength;
  }

  /**
   */
  get id(): string {
    if (this.minLength && this.maxLength && this.minLength === this.maxLength) {
      return INVALID_LENGTH_ID;
    } else if (this.minLength && this.maxLength) {
      return INVALID_LENGTH_BETWEEN_ID;
    } else if (this.minLength) {
      return INVALID_LENGTH_TOO_SHORT_ID;
    } else if (this.maxLength) {
      return INVALID_LENGTH_TOO_LONG_ID;
    }

    return "Constraint.NoLengthConstraint";
  }

  /**
   */
  get minLength(): null | number {
    return this._minLength;
  }

  /**
   */
  get maxLength(): null | number {
    return this._maxLength;
  }

  /**
   */
  hasValidation(): boolean {
    return true;
  }

  /**
   */
  isExactLength(value: string): boolean {
    if (typeof value !== "string") {
      throw new TypeError("isExactLength: Not comparing to string");
    }

    if (this.minLength === null) {
      throw new Error("isExactLength: Min and max length not set");
    }

    return value.length === this.minLength;
  }

  /**
   */
  isBetween(value: string): boolean {
    if (typeof value !== "string") {
      throw new TypeError("isBetween: Not comparing to string");
    }

    if (this.minLength === null || this.maxLength === null) {
      throw new Error("isBetween: Min or max length not set");
    }

    return this.isLongEnough(value) && this.isSmallEnough(value);
  }

  /**
   */
  isLongEnough(value: string): boolean {
    if (typeof value !== "string") {
      throw new TypeError("isLongEnough: Not comparing to string");
    }

    if (this.minLength === null) {
      throw new Error("isLongEnough: Min length not set");
    }

    return value.length >= this.minLength;
  }

  /**
   */
  isSmallEnough(value: string): boolean {
    if (typeof value !== "string") {
      throw new TypeError("isSmallEnough: Not comparing to string");
    }

    if (this.maxLength === null) {
      throw new Error("isSmallEnough: Max length not set");
    }

    return value.length <= this.maxLength;
  }

  /**
   */
  get defaultMessage(): string {
    switch (this.id) {
      case INVALID_LENGTH_ID:
        return "Length must be precisely ${length} characters"; // NOSONAR
      case INVALID_LENGTH_BETWEEN_ID:
        return "Length must be between ${min-length} and ${max-length} characters"; // NOSONAR
      case INVALID_LENGTH_TOO_SHORT_ID:
        return "The length of the value  must be larger than ${min-length}"; // NOSONAR
      case INVALID_LENGTH_TOO_LONG_ID:
        return "The length of the value must be smaller than ${max-length}"; // NOSONAR
      default:
        return "";
    }
  }

  /**
   */
  get parameters(): { ... } {
    switch (this.id) {
      case INVALID_LENGTH_ID:
        return {
          length: this.minLength,
        };
      case INVALID_LENGTH_BETWEEN_ID:
        return {
          "min-length": this.minLength,
          "max-length": this.maxLength,
        };
      case INVALID_LENGTH_TOO_SHORT_ID:
        return {
          "min-length": this.minLength,
        };
      case INVALID_LENGTH_TOO_LONG_ID:
        return {
          "max-length": this.maxLength,
        };
      default:
        return {};
    }
  }

  /**
   */
  validate(value: string): boolean {
    switch (this.id) {
      case INVALID_LENGTH_ID:
        return this.isExactLength(value);
      case INVALID_LENGTH_BETWEEN_ID:
        return this.isBetween(value);
      case INVALID_LENGTH_TOO_SHORT_ID:
        return this.isLongEnough(value);
      case INVALID_LENGTH_TOO_LONG_ID:
        return this.isSmallEnough(value);
      default:
        return true;
    }
  }

  /**
   */
  get isMandatoryConstraint(): boolean {
    return false;
  }
}

export default StringLengthConstraint;
